import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@project/components/ui/dialog";
import { Button } from "@project/components/ui/button";
import { Input } from "@project/components/ui/input";
import { Label } from "@project/components/ui/label";
import { Textarea } from "@project/components/ui/textarea";
import {
  Briefcase,
  Send,
  CheckCircle2,
  Phone,
  User,
  Sparkles,
  Check,
  HeartHandshake,
} from "lucide-react";
import { projectToast } from "@project/lib/toast";
import { useI18n } from "@project/lib/use-i18n";
import { employmentInquiryService } from "@project/services/user";

export interface GruhUdhyogEmploymentModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function GruhUdhyogEmploymentModal({
  open,
  onOpenChange,
}: GruhUdhyogEmploymentModalProps) {
  const { t } = useI18n();
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const [form, setForm] = useState({
    name: "",
    phone: "",
    lookingForEmployment: "yes" as "yes" | "no",
    details: "",
  });

  const resetForm = () => {
    setForm({
      name: "",
      phone: "",
      lookingForEmployment: "yes",
      details: "",
    });
    setSubmitted(false);
  };

  const handleClose = (newOpen: boolean) => {
    onOpenChange(newOpen);
    if (!newOpen) {
      setTimeout(() => {
        resetForm();
      }, 300);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!form.name.trim()) {
      projectToast.error(
        t("account.visitorInquiryForm.validation.nameRequired") ||
          "Please enter your name"
      );
      return;
    }
    const cleanPhone = form.phone.replace(/\D/g, "");
    if (!form.phone.trim() || cleanPhone.length < 10) {
      projectToast.error("Please enter a valid 10-digit mobile number");
      return;
    }

    setSubmitting(true);
    try {
      await employmentInquiryService.create({
        name: form.name.trim(),
        mobile_number: form.phone.trim(),
        is_looking_for_employment: form.lookingForEmployment === "yes",
        additional_details: form.details.trim() || undefined,
      });
      setSubmitted(true);
      projectToast.success(t("programs.gruhUdhyog.employment.successMessage"));
    } catch (err: unknown) {
      const axiosErr = err as {
        response?: { data?: { message?: string; detail?: string } };
        message?: string;
      };
      const errorMsg =
        axiosErr?.response?.data?.message ||
        axiosErr?.response?.data?.detail ||
        axiosErr?.message ||
        "Could not submit inquiry. Please try again or call directly.";
      projectToast.error(errorMsg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-h-[92vh] overflow-y-auto sm:max-w-xl rounded-3xl p-6 sm:p-8 bg-card border-border">
        {submitted ? (
          <div className="py-8 text-center space-y-4">
            <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl bg-amber-500/15 text-amber-600 dark:text-amber-400">
              <CheckCircle2 className="h-10 w-10" />
            </div>
            <h3 className="text-2xl font-bold font-serif text-foreground">
              {t("programs.gruhUdhyog.employment.successTitle")}
            </h3>
            <p className="mx-auto max-w-md text-sm text-muted-foreground leading-relaxed">
              {t("programs.gruhUdhyog.employment.successMessage")}
            </p>

            <div className="mt-6 rounded-2xl border border-border bg-secondary/30 p-4 text-left">
              <p className="text-xs font-semibold text-foreground mb-3">
                {t("programs.gruhUdhyog.employment.callDirect")}
              </p>
              <div className="flex flex-wrap gap-2">
                <a
                  href="tel:+919662544565"
                  className="flex items-center gap-2 rounded-xl bg-background border border-border px-3 py-2 text-xs font-bold text-foreground hover:border-amber-600 hover:text-amber-600 transition-colors"
                >
                  <Phone className="h-3.5 w-3.5 text-amber-600" /> +91 96625 44565
                </a>
                <a
                  href="tel:+919978930960"
                  className="flex items-center gap-2 rounded-xl bg-background border border-border px-3 py-2 text-xs font-bold text-foreground hover:border-amber-600 hover:text-amber-600 transition-colors"
                >
                  <Phone className="h-3.5 w-3.5 text-amber-600" /> +91 99789 30960
                </a>
              </div>
            </div>

            <Button
              className="mt-4 rounded-full bg-amber-700 hover:bg-amber-800 text-white px-8"
              onClick={() => handleClose(false)}
            >
              Close
            </Button>
          </div>
        ) : (
          <>
            <DialogHeader className="space-y-2 text-left">
              <div className="inline-flex items-center gap-2 self-start rounded-full bg-amber-500/10 px-3.5 py-1 text-xs font-bold text-amber-800 dark:text-amber-300">
                <Briefcase className="h-3.5 w-3.5" />
                <span>{t("programs.gruhUdhyog.employment.badge")}</span>
              </div>
              <DialogTitle className="text-2xl font-bold font-serif text-foreground">
                {t("programs.gruhUdhyog.employment.modalTitle")}
              </DialogTitle>
              <p className="text-xs text-muted-foreground">
                {t("programs.gruhUdhyog.employment.modalSubtitle")}
              </p>
            </DialogHeader>

            <form onSubmit={handleSubmit} className="mt-4 space-y-4">
              {/* Full Name & Mobile Number */}
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-1.5">
                  <Label htmlFor="employment-name" className="text-xs font-semibold">
                    {t("programs.gruhUdhyog.employment.fullName")}
                  </Label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                    <Input
                      id="employment-name"
                      placeholder={t("programs.gruhUdhyog.employment.fullNamePlaceholder")}
                      value={form.name}
                      onChange={(e) => setForm({ ...form, name: e.target.value })}
                      className="pl-9 rounded-xl"
                      required
                    />
                  </div>
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="employment-phone" className="text-xs font-semibold">
                    {t("programs.gruhUdhyog.employment.phoneNumber")}
                  </Label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                    <Input
                      id="employment-phone"
                      type="tel"
                      placeholder={t("programs.gruhUdhyog.employment.phoneNumberPlaceholder")}
                      value={form.phone}
                      onChange={(e) => setForm({ ...form, phone: e.target.value })}
                      className="pl-9 rounded-xl"
                      required
                    />
                  </div>
                </div>
              </div>

              {/* રોજગારી મેળવવા માંગો છો? (Are you looking for employment?) */}
              <div className="space-y-2">
                <Label className="text-xs font-semibold text-foreground flex items-center justify-between">
                  <span>
                    {t("programs.gruhUdhyog.employment.lookingForEmployment")} <span className="text-rose-500">*</span>
                  </span>
                </Label>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                  <button
                    type="button"
                    onClick={() => setForm({ ...form, lookingForEmployment: "yes" })}
                    className={`relative flex flex-col items-start p-3.5 rounded-2xl border text-left transition-all ${
                      form.lookingForEmployment === "yes"
                        ? "border-amber-600/80 bg-amber-500/10 text-foreground ring-1 ring-amber-600/50 shadow-xs"
                        : "border-border bg-background hover:bg-secondary/40 text-muted-foreground"
                    }`}
                  >
                    <div className="flex w-full items-center justify-between">
                      <span className="text-xs font-bold text-foreground flex items-center gap-1.5">
                        <HeartHandshake className="h-4 w-4 text-amber-600" />
                        {t("programs.gruhUdhyog.employment.lookingYes")}
                      </span>
                      {form.lookingForEmployment === "yes" && (
                        <div className="h-4.5 w-4.5 rounded-full bg-amber-600 text-white flex items-center justify-center shrink-0">
                          <Check className="h-3 w-3 stroke-[3]" />
                        </div>
                      )}
                    </div>
                    <span className="mt-1 text-[11px] text-muted-foreground">
                      {t("programs.gruhUdhyog.employment.lookingYesDesc")}
                    </span>
                  </button>

                  <button
                    type="button"
                    onClick={() => setForm({ ...form, lookingForEmployment: "no" })}
                    className={`relative flex flex-col items-start p-3.5 rounded-2xl border text-left transition-all ${
                      form.lookingForEmployment === "no"
                        ? "border-amber-600/80 bg-amber-500/10 text-foreground ring-1 ring-amber-600/50 shadow-xs"
                        : "border-border bg-background hover:bg-secondary/40 text-muted-foreground"
                    }`}
                  >
                    <div className="flex w-full items-center justify-between">
                      <span className="text-xs font-bold text-foreground">
                        {t("programs.gruhUdhyog.employment.lookingNo")}
                      </span>
                      {form.lookingForEmployment === "no" && (
                        <div className="h-4.5 w-4.5 rounded-full bg-amber-600 text-white flex items-center justify-center shrink-0">
                          <Check className="h-3 w-3 stroke-[3]" />
                        </div>
                      )}
                    </div>
                    <span className="mt-1 text-[11px] text-muted-foreground">
                      {t("programs.gruhUdhyog.employment.lookingNoDesc")}
                    </span>
                  </button>
                </div>
              </div>

              {/* Additional Details */}
              <div className="space-y-1.5">
                <Label htmlFor="employment-details" className="text-xs font-semibold">
                  {t("programs.gruhUdhyog.employment.additionalDetails")}
                </Label>
                <Textarea
                  id="employment-details"
                  rows={3}
                  placeholder={t("programs.gruhUdhyog.employment.additionalDetailsPlaceholder")}
                  value={form.details}
                  onChange={(e) => setForm({ ...form, details: e.target.value })}
                  className="rounded-xl resize-none"
                />
              </div>

              {/* Footer info & submit */}
              <div className="pt-2 flex flex-col sm:flex-row gap-3 items-center justify-between">
                <div className="text-[11px] text-muted-foreground flex items-center gap-1">
                  <Sparkles className="h-3.5 w-3.5 text-amber-500" />
                  100% Free Application • Dignified Livelihood
                </div>
                <Button
                  type="submit"
                  disabled={submitting}
                  className="w-full sm:w-auto rounded-full bg-amber-700 hover:bg-amber-800 text-white px-7 font-bold text-sm shadow-md transition-all"
                >
                  {submitting ? (
                    t("programs.gruhUdhyog.employment.submitting")
                  ) : (
                    <span className="flex items-center gap-2">
                      <Send className="h-4 w-4" />
                      {t("programs.gruhUdhyog.employment.submit")}
                    </span>
                  )}
                </Button>
              </div>
            </form>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
